import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Chip, type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { BADGE } from "../../../theme/tokens";
import { ChangesSource } from "../../../utils/models";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";

type Props = {
	/** A change/activity-log source value (e.g. "Profile", "Assistant", "Nurse App"). */
	source: string | null | undefined;
	/** Rendered when `source` is empty. Defaults to an em dash. */
	emptyLabel?: string;
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

/**
 * Renders a change-log/activity-log `source`. When the source is the AI assistant it becomes a
 * primary-tinted "AI Assistant" chip (sparkle + brand tint, echoing the app's Annie/AI language)
 * so assistant-driven changes stand out; every other source renders as plain text.
 */
export const ChangeSourceBadge: FC<Props> = ({ source, emptyLabel = "—", sx, dataTestId }) => {
	const { t } = useTranslation();

	if (source !== ChangesSource.Assistant) {
		return <>{source || emptyLabel}</>;
	}

	return (
		<InHouseTooltip title={t("badge.aiAssistant.tooltip")} arrow>
			<Chip
				icon={<AutoAwesomeIcon />}
				label={t("badge.aiAssistant")}
				size="small"
				data-testid={dataTestId}
				sx={[
					(theme) => ({
						height: BADGE.STATUS_CHIP_HEIGHT_SM,
						fontWeight: 600,
						fontSize: theme.typography.caption.fontSize,
						color: theme.palette.primary.main,
						backgroundColor: alpha(theme.palette.primary.main, 0.12),
						border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
						"& .MuiChip-label": { px: 1 },
						"& .MuiChip-icon": { color: theme.palette.primary.main, ml: 0.75, mr: -0.25, fontSize: "0.95rem" },
					}),
					...(Array.isArray(sx) ? sx : [sx]),
				]}
			/>
		</InHouseTooltip>
	);
};
